import React, { useState, useEffect } from 'react';
import { Package, Plus, CheckCircle, Camera, Clock, Search, User, X, MessageCircle, AlertTriangle, Trash2, Archive } from 'lucide-react';
import api, { getAssetUrl } from '../../services/api';
import EntregaModal from './EntregaModal';
import RetiradaModal from './RetiradaModal';
import { formatDateTimeSaoPaulo } from '../../utils/formatters';

const EntregaPage = () => {
  const [entregas, setEntregas] = useState([]);
  const [moradores, setMoradores] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [entregaRetirada, setEntregaRetirada] = useState(null);
  const [imagemExpandida, setImagemExpandida] = useState(null);
  const [abaAtiva, setAbaAtiva] = useState('pendentes'); // 'pendentes' ou 'concluidas'
  const [searchTerm, setSearchTerm] = useState('');
  const [limpandoHistorico, setLimpandoHistorico] = useState(false);

  const fetchData = async () => {
    try {
      const [resEntregas, resMoradores] = await Promise.all([
        api.get('/entregas/'),
        api.get('/entregas/moradores')
      ]);
      setEntregas(resEntregas.data);
      setMoradores(resMoradores.data);
    } catch (error) {
      console.error("Erro ao carregar entregas:", error);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleBaixa = (entrega) => {
    setEntregaRetirada(entrega);
  };

  const handleBaixaSuccess = (entregaAtualizada) => {
    setEntregas((atual) =>
      atual.map((entrega) => (entrega.id === entregaAtualizada.id ? entregaAtualizada : entrega))
    );
    setEntregaRetirada(null);
    setAbaAtiva('concluidas');
  };

  const handleNotificar = async (entrega) => {
    try {
      const response = await api.post(`/entregas/${entrega.id}/notificar`);
      setEntregas((atual) =>
        atual.map((item) => (item.id === entrega.id ? response.data.entrega : item))
      );
      alert('Morador notificado no WhatsApp.');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Nao foi possivel enviar o WhatsApp.');
      fetchData();
    }
  };

  const handleLimparHistorico = async () => {
    if (entregasConcluidasFiltradas.length === 0) {
      return;
    }

    const confirmado = window.confirm('Deseja limpar o historico de entregas concluidas? Esta acao nao pode ser desfeita.');
    if (!confirmado) {
      return;
    }

    setLimpandoHistorico(true);
    try {
      await api.delete('/entregas/historico');
      setEntregas((atual) => atual.filter((entrega) => entrega.status !== 'Entregue'));
    } catch (error) {
      const detail = error?.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Nao foi possivel limpar o historico.');
      fetchData();
    } finally {
      setLimpandoHistorico(false);
    }
  };

  const pendentes = entregas.filter(e => e.status === 'Pendente');
  const termoBusca = searchTerm.toLowerCase();
  const filtrarEntregas = (lista) =>
    lista.filter((entrega) =>
      [
        entrega.nome_morador,
        entrega.unidade,
        entrega.empresa,
        entrega.descricao,
        entrega.quem_retirou,
        entrega.documento_quem_retirou
      ].some((valor) => (valor || '').toLowerCase().includes(termoBusca))
    );
  const entregasPendentesFiltradas = filtrarEntregas(pendentes);
  const entregasConcluidasFiltradas = filtrarEntregas(entregas.filter(e => e.status === 'Entregue'))
    .sort((a, b) => new Date(b.data_retirada || 0) - new Date(a.data_retirada || 0));

  const abrirImagem = (src, alt) => {
    if (!src) {
      return;
    }

    setImagemExpandida({ src, alt });
  };


  const renderNotificacao = (entrega) => {
    const status = entrega.notificacao_status || 'pendente';

    if (status === 'enviada') {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700">
          <MessageCircle size={13} /> Avisado
        </span>
      );
    }

    if (status === 'falhou') {
      return (
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-[11px] font-bold text-red-700" title={entrega.notificacao_erro || ''}>
            <AlertTriangle size={13} /> Falhou
          </span>
          {entrega.whatsapp_url && (
            <a
              href={entrega.whatsapp_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-bold text-white transition hover:bg-slate-700"
            >
              <MessageCircle size={13} /> Manual
            </a>
          )}
        </div>
      );
    }

    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-500">
          <Clock size={13} /> Nao avisado
        </span>
        <button
          type="button"
          onClick={() => handleNotificar(entrega)}
          className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-bold text-white transition hover:bg-slate-700"
        >
          <MessageCircle size={13} /> Enviar
        </button>
      </div>
    );
  };

  const renderFotoPessoa = (foto, alt) => {
    if (!foto) {
      return (
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-400">
          <User size={18} />
        </div>
      );
    }

    return (
      <button
        type="button"
        onClick={() => abrirImagem(getAssetUrl(foto), alt)}
        className="overflow-hidden rounded-full ring-2 ring-white transition hover:scale-105"
      >
        <img src={getAssetUrl(foto)} alt={alt} className="h-11 w-11 object-cover" />
      </button>
    );
  };

  return (
    <div className="flex min-h-full flex-col gap-6 px-1 sm:px-2 lg:px-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-800">
            <span className="rounded-lg bg-condo-dark p-2"><Package className="text-white" size={20}/></span>
            Entregas
          </h1>
          <p className="text-sm text-gray-500">Gerencie encomendas e pacotes recebidos</p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:bg-slate-800"
          >
            <Plus size={18} /> Nova entrega
          </button>
          <button
            type="button"
            onClick={() => setAbaAtiva('pendentes')}
            className={`inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold shadow-sm transition ${
              abaAtiva === 'pendentes'
                ? 'border border-slate-900 bg-slate-900 text-white hover:bg-slate-800'
                : 'border border-gray-200 bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Package size={18} /> Entregas ativas
          </button>
          <button
            type="button"
            onClick={() => setAbaAtiva('concluidas')}
            className={`inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold shadow-sm transition ${
              abaAtiva === 'concluidas'
                ? 'border border-slate-900 bg-slate-900 text-white hover:bg-slate-800'
                : 'border border-gray-200 bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Archive size={18} /> Retirados
          </button>
        </div>
      </div>

      <div className="relative mb-6">
        <Search className="absolute left-3 top-3.5 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Buscar entrega por morador, unidade, empresa ou descricao..."
          className="block w-full rounded-xl border border-gray-200 bg-white py-3 pl-10 pr-3 shadow-sm outline-none transition-all focus:ring-2 focus:ring-condo-teal"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
      </div>

      {/* Lógica para exibir a Tabela de Histórico quando a aba for 'concluidas' */}
      {abaAtiva === 'concluidas' ? (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="flex flex-col gap-3 border-b border-gray-100 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-sm font-bold text-slate-800">Historico de entregas</h2>
              <p className="text-xs text-slate-400">{entregasConcluidasFiltradas.length} entrega(s) concluida(s)</p>
            </div>
            <button
              type="button"
              onClick={handleLimparHistorico}
              disabled={limpandoHistorico || entregasConcluidasFiltradas.length === 0}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-50 px-4 py-2 text-sm font-bold text-red-600 transition hover:bg-red-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-red-50 disabled:hover:text-red-600"
            >
              <Trash2 size={17} />
              {limpandoHistorico ? 'Limpando...' : 'Limpar historico'}
            </button>
          </div>
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-gray-400 text-[10px] uppercase font-bold tracking-widest">
              <tr>
                <th className="px-6 py-4">Morador/Unidade</th>
                <th className="px-6 py-4">Empresa</th>
                <th className="px-6 py-4">Chegada</th>
                <th className="px-6 py-4">Retirada por</th>
                <th className="px-6 py-4">WhatsApp</th>
                <th className="px-6 py-4">Data Saída</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {entregasConcluidasFiltradas.map(e => (
                <tr key={e.id} className="text-sm text-slate-600">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {renderFotoPessoa(e.morador_foto_url, `Foto de ${e.nome_morador}`)}
                      <div>
                        <div className="font-bold text-slate-800">{e.nome_morador}</div>
                        <div className="text-[10px] text-slate-400">Unid: {e.unidade}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">{e.empresa}</td>
                  <td className="px-6 py-4 text-xs">{formatDateTimeSaoPaulo(e.data_chegada)}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {renderFotoPessoa(e.foto_retirada_url, `Foto de quem retirou a encomenda de ${e.nome_morador}`)}
                      <div>
                        <div className="font-medium text-green-600">{e.quem_retirou || '-'}</div>
                        <div className="text-xs text-slate-400">{e.documento_quem_retirou || '-'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">{renderNotificacao(e)}</td>
                  <td className="px-6 py-4 text-xs">{formatDateTimeSaoPaulo(e.data_retirada)}</td>
                </tr>
              ))}
              {entregasConcluidasFiltradas.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-20 text-center text-gray-400 italic">
                    Nenhuma entrega encontrada.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        // Grid de Encomendas Moderno
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {entregasPendentesFiltradas.map((e) => (
            <div key={e.id} className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-all group">
              <div className="relative h-40 bg-slate-100">
                {e.foto_url ? (
                  <button
                    type="button"
                    onClick={() => abrirImagem(getAssetUrl(e.foto_url), `Foto da encomenda de ${e.nome_morador}`)}
                    className="h-full w-full cursor-zoom-in"
                    title="Visualizar foto da encomenda"
                  >
                    <img src={getAssetUrl(e.foto_url)} alt="Pacote" className="h-full w-full object-cover" />
                  </button>
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-300"><Camera size={40}/></div>
                )}
                <div className="absolute top-4 left-4">
                  <span className="bg-slate-900/80 backdrop-blur-md text-white text-[10px] px-3 py-1 rounded-full font-bold uppercase">
                    {e.empresa}
                  </span>
                </div>
              </div>
              <div className="p-5">
                <div className="mb-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-bold text-slate-800 text-lg">{e.nome_morador}</h3>
                      <p className="text-slate-500 text-xs font-medium uppercase tracking-wider">Unidade: {e.unidade}</p>
                    </div>
                    {renderNotificacao(e)}
                  </div>
                </div>
                <p className="text-slate-600 text-sm mb-5 italic">"{e.descricao}"</p>
                <button 
                  onClick={() => handleBaixa(e)}
                  className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold text-sm hover:bg-slate-800 transition-all flex items-center justify-center gap-2 shadow-sm active:scale-[0.99]"
                >
                  <CheckCircle size={18} /> Dar Baixa (Entregar)
                </button>
              </div>
            </div>
          ))}
          {entregasPendentesFiltradas.length === 0 && (
            <div className="col-span-full rounded-3xl border border-gray-100 bg-white px-6 py-20 text-center text-gray-400 italic shadow-sm">
              Nenhuma entrega encontrada.
            </div>
          )}
        </div>
      )}

      <EntregaModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        moradores={moradores}
        refreshData={fetchData}
      />
      <RetiradaModal
        entrega={entregaRetirada}
        isOpen={Boolean(entregaRetirada)}
        onClose={() => setEntregaRetirada(null)}
        onSuccess={handleBaixaSuccess}
        moradores={moradores}
      />
      {imagemExpandida && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          onClick={() => setImagemExpandida(null)}
        >
          <button
            type="button"
            onClick={() => setImagemExpandida(null)}
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20"
          >
            <X size={22} />
          </button>
          <img
            src={imagemExpandida.src}
            alt={imagemExpandida.alt}
            className="max-h-[90vh] max-w-[90vw] rounded-2xl object-contain shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default EntregaPage;
